<template>
  <div class="leftCardContainer">
    <div class="rightSideTitleActions">
      <span class="leftTitle">{{ $tc('Import Templates', 2) }}</span>
      <div class="actionsContainer">
        <div class="searchContainer">
          <v-icon> search </v-icon>
          <InputField
            inputType="text"
            :value="search"
            :placeholder="$t('Search')"
            :inputStyle="{
              borderRadius: '0px',
              width: '130px',
              borderTop: 'none',
              borderRight: 'none',
              borderLeft: 'none',
              borderBottom: '1px solid rgba(118, 118, 118, 0.50)',
            }"
            @input="handleSearch($event)" />
        </div>
        <CustomButton
          icon="add"
          :width="'20px'"
          :height="'20px'"
          :iconSize="12"
          :buttonStyling="{ padding: '8px', marginRight: '5px', borderRadius: '4px' }"
          :disabled="!userHasPermission('company_store') || userIsCreatingOrEditingData"
          @buttonClick="handleAdd"
        />
      </div>
    </div>
    <div class="tableDivContainer">
      <Table
        :checkbox="false"
        :loading="loading"
        :headers="headers"
        :items="templates"
        :sortable="true"
        :emitRowClickBusEvent="!userIsCreatingOrEditingData"
        :scroll-id="scrollId"
        @onScrollBottom="onScrollBottom"
        @onScrollTop="onScrollTop"
        @sortClick="onSortClick"
      />
    </div>
    <loading-spinner
      :active.sync="loading"
      :is-full-page="false"
      color="var(--app-primary-base)"
      style="position: absolute;"
    />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { bus } from "@/main.js";
import { managementDatatableMixin } from "@/mixins/management/managementDatatableMixin";
import ManagementPage from "@/newComponents/_Organisms/ManagementPage.vue";
import Table from "@/newComponents/_Molecules/Table.vue";
import CustomButton from "@/newComponents/_Atoms/CustomButton.vue";
import InputField from "@/newComponents/_Atoms/InputField.vue";
import Tabs from "@/newComponents/_Atoms/Tabs.vue";
import DialogContent from "@/newComponents/_Molecules/DialogContent.vue";
import LoadingSpinner from "vue-loading-overlay";
import DialogChoice from '@/newComponents/_Molecules/DialogChoice.vue';
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";
import { importMixin } from "@/mixins/management/importMixin";

export default {
  name: "TableView",

  mixins: [managementDatatableMixin, checkPermissionsMixin, importMixin],

  components: {
    Table,
    ManagementPage,
    CustomButton,
    InputField,
    Tabs,
    DialogContent,
    LoadingSpinner,
    DialogChoice,
  },

  props: {
    selectedTemplate: {
      type: Object,
    },
  },

  data() {
    return {
      dialog: false,
      deleteDialogIsOpen: false,
      value: null,
      type: "normal",
      search: null,
      activeTab: 0,
      loading: false,
      scrollTop: 0,
      sortOrder: null,
      scrollHeight: 0,
      pageNumber: 1,
      pagination: 25,
      currentPageTop: 1,
      currentPageBot: 1,
      lastPage: 2,
      templates: [],
      permissionName: "import_mapper",
      userIsCreatingOrEditingData: false,
    };
  },

  mounted() {
    this.getInitialTemplates();
    bus.$on("reload-templates", () => this.getInitialTemplates());
    bus.$on("toggle-create-edit-flag", () => this.userIsCreatingOrEditingData = !this.userIsCreatingOrEditingData)
  },

  destroyed() {
    bus.$off("reload-templates");
    bus.$off("toggle-create-edit-flag");
  },

  computed: {
    headers() {
      return [
        { text: this.$i18n.tc("Label", 1), varName: "name" },
        { text: this.$i18n.tc("Layer", 1), varName: "layer" },
        { text: this.$i18n.tc("Blocked", 1), varName: "blocked" },
      ]
    },

    scrollId() {
      return 'scroll-bar-templates';
    },
  },

  methods: {
    ...mapActions(["fetchImportTemplates"]),

    closeDialog() {
      this.dialog = false;
    },

    async getInitialTemplates() {
      this.loading = true;
      this.currentPageBot = 1
      this.currentPageTop = 1
      let queryParams = {
        "page[number]": 1,
        paginate: 25,
        append: "type"
      };

      if (this.search !== null) {
        queryParams["filter[search]"] = this.search;
      }

      if (this.sortOrder !== null) {
        queryParams["sort"] = this.sortOrder;
      }

      this.getImportTemplates(queryParams);
    },

    getTemplatesBottom() {
      this.loading = true;
      this.pagination = 25;
      if (this.templates.length >= 3*this.pagination) {
        this.currentPageTop++;
      }
      this.currentPageBot++;

      let queryParams = {
        "page[number]": this.currentPageBot,
        paginate: this.pagination,
        append: "type"
      };

      if (this.search !== null) {
        queryParams["filter[search]"] = this.search;
      }

      if (this.sortOrder !== null) {
        queryParams["sort"] = this.sortOrder;
      }

      this.getImportTemplates(queryParams, "bottom");
    },

    async getTemplatesTop() {
      this.loading = true;
      this.pagination = 25;
      this.currentPageTop--;

      let queryParams = {
        "page[number]": this.currentPageTop,
        paginate: this.pagination,
        append: "type"
      };

      if (this.search !== null) {
        queryParams["filter[search]"] = this.search;
      }

      if (this.sortOrder !== null) {
        queryParams["sort"] = this.sortOrder;
      }

      this.getImportTemplates(queryParams, "top");
    },


    async getImportTemplates(queryParams = null, scrollDirection = null) {
      this.loading = true;
      try {
        let importTemplates = await this.fetchImportTemplates(queryParams);
        this.processImportTemplatesData(importTemplates, scrollDirection);
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
        this.$nextTick(() =>
          this.updateLocalPagination(this.paginationFromParent)
        );
      }
    },

    processImportTemplatesData(importTemplatesData, scrollDirection) {
      let allImportTemplates = null;
      let currentScroll = null;
      let rowElement = null;
      switch (scrollDirection) {
        case 'top':
          this.lastPage = importTemplatesData.meta.last_page;
          allImportTemplates = [...importTemplatesData.data, ...this.templates];
          this.templates = Array.from(new Set(allImportTemplates.map(a => a.id)))
            .map(id => { return allImportTemplates.find(a => a.id === id) })
          if (this.templates.length > 3*this.pagination) {
            this.templates.splice(-(this.templates.length - 3*this.pagination));
            this.currentPageBot--;
          }

          currentScroll = document.getElementById(this.scrollId).scrollTop;
          this.loading = false;

          rowElement = document.getElementById("rowId");
          if (rowElement) {
            const rowHeight = rowElement.clientHeight;
            for (let i = 0; i < this.pagination; i++) {
              const offset = rowHeight * i;
              document.getElementById(this.scrollId).scrollTo({
                top: currentScroll + offset,
                behavior: "smooth",
              });
            }
          }
          break;
        case 'bottom':
          this.lastPage = importTemplatesData.meta.last_page;
          allImportTemplates = [...this.templates, ...importTemplatesData.data];
          this.templates = Array.from(new Set(allImportTemplates.map(a => a.id)))
            .map(id => { return allImportTemplates.find(a => a.id === id) })
          if (this.templates.length > 3*this.pagination) {
            this.templates = this.templates.slice(this.templates.length - 3*this.pagination);
          }

          currentScroll = document.getElementById(this.scrollId).scrollTop;
          this.loading = false;

          rowElement = document.getElementById("rowId");
          if (rowElement) {
            const rowHeight = rowElement.clientHeight;
            for (let i = 0; i < this.pagination; i++) {
              const offset = rowHeight * i;
              document.getElementById(this.scrollId).scrollTo({
                top: currentScroll - offset,
                behavior: "smooth",
              });
            }
          }
          break;
        default:
          this.templates = importTemplatesData.data;
          this.templates.forEach(template => {
            template.layer = this.getPossibleLayers(template.type[0].name);
          });
          this.lastPage = importTemplatesData.meta.last_page;
          break;
      }
    },

    getPossibleLayers(layer) {
      if (layer) {
        let translation = this.$store.getters.getLayerTranslationKey(layer);
        return this.$i18n.tc(translation, 2);
      } else {
        return null;
      }
    },

    handleSearch: _.debounce(function (event) {
      if (event === "") {
        this.search = null;
      } else {
        this.search = event;
      }
      this.templates = [];
      this.getInitialTemplates();
    }, 1000),

    onScrollBottom() {
      if (this.currentPageBot !== this.lastPage) {
        this.pageNumber++;
        this.getTemplatesBottom();
      }
    },

    onScrollTop() {
      if (this.currentPageTop !== 1) {
        this.pageNumber--;
        this.getTemplatesTop();
      }
    },

    onSortClick(sortOrder, index) {
      this.templates = []
      if (sortOrder === "asc") {
        this.sortOrder = this.headers[index].varName;
      } else if (sortOrder === "desc") {
        this.sortOrder = `-${this.headers[index].varName}`;
      } else {
        this.sortOrder = sortOrder;
      }
      this.getInitialTemplates();
    },

    handleAdd() {
      this.$emit('open-create-import-form')
    },

    openDeleteDialog(){
      if (this.selectedObjectID) {
        this.deleteDialogIsOpen = true;
      }
    },

  },
};
</script>

<style scoped>
.searchContainer {
  display: flex;
  align-items: center;
}

.tableDivContainer {
  height: 87%;
  margin-top: 5px;
}

.tabsContainer {
  height: 8%;
  display: flex;
  margin-top: 5px;
}

.leftCardContainer {
  width: 100%;
  height: 100%;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0px 2px 2px 0px rgba(0, 0, 0, 0.25);
  padding: 20px 28px;
}

.actionsContainer {
  display: flex;
  align-items: center;
  gap: 3px;
}

.leftTitle {
  color: #767676;
  font-family: Lato;
  font-size: 10px;
  font-style: normal;
  font-weight: 800;
  line-height: 20px;
  text-transform: uppercase;
}

.rightSideTitleActions {
  height: 5%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.buttons-container-custom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 20px;
}

.cancel-button-custom {
  background: transparent;
  border-radius: 0.25rem;
  border: 0.609px solid var(--app-primary-base);
}

.leftCardContainer {
  width: 100%;
  height: 100%;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0px 2px 2px 0px rgba(0, 0, 0, 0.25);
  padding: 20px 28px;
}

.actionsContainer {
  display: flex;
  align-items: center;
  gap: 3px;
}

.leftTitle {
  color: #767676;
  font-family: Lato;
  font-size: 10px;
  font-style: normal;
  font-weight: 800;
  line-height: 20px;
  /* 200% */
  text-transform: uppercase;
}

.rightSideTitleActions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.layer-search {
  flex: 0 0 auto;
  font-size: 12px;
  margin-right: 5px;
}

.layers-search .v-input__slot {
  min-height: 0 !important;
}

.layers-search .v-label {
  top: 4px !important;
  font: normal normal normal 12px/16px Segoe UI;
  color: #b1b6b9;
}

.span-custom {
  display: flex;
  padding: 0px 20px;
  color: #767676;
  font-family: Lato;
  font-size: 0.625rem;
  font-style: normal;
  font-weight: 400;
  line-height: 0.9375rem;
}

.tabs-custom {
  padding: 0px 20px;
}

.table-custom {
  height: 90%;
}

.table-buttons-custom {
  height: 70%;
}
</style>



// WEBPACK FOOTER //
// src/newComponents/WorkspaceManagement/Templates/TableView.vue